import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Loader2, X } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

export function ImageUpload({ value, onChange, label = 'Upload Photo', shape = 'circle', className = '' }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Important: do NOT hardcode Content-Type — axios auto-sets it with the correct multipart boundary.
      const { data } = await api.post('/upload/image', formData);
      onChange(data.url);
      toast.success('Image uploaded!');
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      toast.error(msg);
    } finally {
      setUploading(false);
      // Reset input so selecting the same file again still triggers onChange
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const imageUrl = value && value.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;

  return (
    <div className={`flex flex-col items-center gap-2 ${className}`} data-testid="image-upload">
      <div
        className={`relative overflow-hidden bg-slate-100 flex items-center justify-center cursor-pointer border-2 border-dashed border-slate-200 hover:border-[#56dfb4] transition-colors ${
          shape === 'circle' ? 'w-28 h-28 rounded-full' : 'w-full h-40 rounded-xl'
        }`}
        onClick={() => inputRef.current?.click()}
      >
        {imageUrl ? (
          <>
            <img src={imageUrl} alt="Upload" className="w-full h-full object-cover" />
            <button onClick={(e) => { e.stopPropagation(); onChange(''); }} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70" data-testid="image-remove-btn">
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <div className="text-center p-3">
            {uploading ? <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" /> : <Camera className="w-6 h-6 text-slate-400 mx-auto" />}
            <p className="text-xs text-slate-400 mt-1">{uploading ? 'Uploading...' : label}</p>
          </div>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" data-testid="image-upload-input" />
      {!value && <Button type="button" variant="outline" size="sm" className="rounded-full text-xs gap-1" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="image-upload-btn">
        <Camera className="w-3 h-3" />{label}
      </Button>}
    </div>
  );
}
